// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type {
  AccountInfo,
  Configuration,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import {
  configuredTenantId,
  configuredTokenIssuers,
  getEnvValue,
  parseApiScopes,
  resolveAuthority,
  resolveAuthorityHost,
  tenantMatchesAccount,
} from "./utils/authConfigLogic";
import {
  isDevelopmentMode,
  isTestMode,
  readRuntimeEnvRecords,
} from "./utils/runtimeEnv";

export function createMsalConfig(): Configuration {
  const envs = readRuntimeEnvRecords();
  const authority = resolveAuthority(envs);
  return {
    auth: {
      clientId: getEnvValue(envs, "VITE_ENTRA_CLIENT_ID") ?? "",
      authority,
      knownAuthorities: [resolveAuthorityHost(authority)],
      redirectUri:
        getEnvValue(envs, "VITE_ENTRA_REDIRECT_URI") ?? window.location.origin,
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
  };
}

export function createLoginRequest(): RedirectRequest {
  return { scopes: parseApiScopes(readRuntimeEnvRecords()) };
}

export function createTokenRequest(): PopupRequest {
  return { scopes: parseApiScopes(readRuntimeEnvRecords()) };
}

export function getApiBaseUrl(): string {
  return getEnvValue(readRuntimeEnvRecords(), "VITE_API_BASE_URL") ?? "";
}

export function getConfiguredAuthorityTenantId(): string | undefined {
  return configuredTenantId(readRuntimeEnvRecords());
}

export function getExpectedTokenIssuers(): string[] {
  return configuredTokenIssuers(readRuntimeEnvRecords());
}

export function allowsUnresolvedTenant(): boolean {
  return isTestMode() || isDevelopmentMode();
}

export function isAccountForConfiguredAuthority(account: AccountInfo): boolean {
  const tenantId = getConfiguredAuthorityTenantId();
  return tenantId === undefined
    ? allowsUnresolvedTenant()
    : tenantMatchesAccount(account, tenantId);
}
